'use client';

import { useEffect, useState } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@/components/ui/carousel';
import { CldImage } from 'next-cloudinary';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export interface PropertyInfoWindowProperty {
  id: string;
  title: string;
  pin: { lat: number; lng: number };
  images: string[];
  basePrice: number;
  beds: number;
  baths: number;
  maxGuests: number;
  area: { label: string };
  destination: { label: string };
  distance: number;
}

export default function PropertyInfoWindow({
  property,
  referral,
}: {
  property: PropertyInfoWindowProperty;
  referral: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const handlePrev = () => {
    api?.scrollPrev();
  };

  const handleNext = () => {
    api?.scrollNext();
  };

  const propertyUrl = referral
    ? `/properties/${property.id}?referral=${encodeURIComponent(referral)}`
    : `/properties/${property.id}`;

  const hasImages = property.images && property.images.length > 0;

  return (
    <div className="md:p-2 max-w-xs">
      {hasImages && (
        <div className="relative mb-2 rounded-lg overflow-hidden">
          <Carousel
            setApi={setApi}
            opts={{
              loop: property.images.length > 1,
            }}
            className="w-full"
          >
            <CarouselContent>
              {property.images.map((image, index) => (
                <CarouselItem key={index} className="basis-full">
                  <div className="relative w-full h-40">
                    <CldImage
                      src={image}
                      alt={`${property.title} - Image ${index + 1}`}
                      width={300}
                      height={160}
                      className="object-cover w-full h-full rounded-lg"
                      loading={index === 0 ? 'eager' : 'lazy'}
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>

            {property.images.length > 1 && (
              <>
                {current > 0 && (
                  <button
                    onClick={handlePrev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-1.5 shadow-md transition-all z-10"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}

                {current < property.images.length - 1 && (
                  <button
                    onClick={handleNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-1.5 shadow-md transition-all z-10"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}

                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                  {current + 1} / {property.images.length}
                </div>
              </>
            )}
          </Carousel>
        </div>
      )}

      <h3 className="font-semibold text-sm mb-1">{property.title}</h3>
      <p className="text-sm text-gray-600 mb-1">
        {property.destination.label} • {property.area.label}
      </p>
      <p className="text-sm text-gray-600 mb-1">
        {property.beds} beds • {property.baths} baths • {property.maxGuests} guests
      </p>
      <p className="text-sm font-semibold text-primary mb-2">
        EGP {property.basePrice.toLocaleString()} / night
      </p>
      {property.distance && (
        <p className="text-sm text-gray-500 mb-2">{property.distance.toFixed(1)} km away</p>
      )}
      <a
        href={propertyUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-blue-600 hover:underline mt-2 inline-block"
      >
        View Property →
      </a>
    </div>
  );
}
